import { MeasureValue } from "~/types";
import { calculatePercentChange } from "../calculatePercentChange";

/**
 * Merges multiple MeasureValue objects into a single MeasureValue by summing
 * current and historical values, then recalculating percentage changes.
 * Returns null if all input values are null.
 */
export function mergeMeasureValues(
    values: Array<MeasureValue | null>,
): MeasureValue | null {
    // Filter out null values
    const validValues = values.filter(
        (v): v is NonNullable<MeasureValue> => v !== null,
    );

    // Return null if no valid values
    if (validValues.length === 0) {
        return null;
    }

    // Sum all current values
    const val = validValues.reduce((sum, v) => sum + v.val, 0);

    // Sum all historical values (handling nulls)
    const val_7d_values = validValues
        .map((v) => v.val_7d)
        .filter((v): v is number => v !== null);
    const val_30d_values = validValues
        .map((v) => v.val_30d)
        .filter((v): v is number => v !== null);
    const val_90d_values = validValues
        .map((v) => v.val_90d)
        .filter((v): v is number => v !== null);

    const val_7d =
        val_7d_values.length > 0
            ? val_7d_values.reduce((sum, v) => sum + v, 0)
            : null;
    const val_30d =
        val_30d_values.length > 0
            ? val_30d_values.reduce((sum, v) => sum + v, 0)
            : null;
    const val_90d =
        val_90d_values.length > 0
            ? val_90d_values.reduce((sum, v) => sum + v, 0)
            : null;

    // Calculate changes
    const chg_7d_amt = val_7d !== null ? val - val_7d : null;
    const chg_7d_pct =
        val_7d !== null
            ? calculatePercentChange({ initialValue: val_7d, finalValue: val })
            : null;

    const chg_30d_amt = val_30d !== null ? val - val_30d : null;
    const chg_30d_pct =
        val_30d !== null
            ? calculatePercentChange({ initialValue: val_30d, finalValue: val })
            : null;

    const chg_90d_amt = val_90d !== null ? val - val_90d : null;
    const chg_90d_pct =
        val_90d !== null
            ? calculatePercentChange({ initialValue: val_90d, finalValue: val })
            : null;

    return {
        val,
        val_7d,
        val_30d,
        val_90d,
        chg_7d_amt,
        chg_7d_pct,
        chg_30d_amt,
        chg_30d_pct,
        chg_90d_amt,
        chg_90d_pct,
    };
}
